// Partner applications — triage for the public "Become a Partner Agency"
// form (app.partner_applications). On approval, create the real partner
// (commission/secret/deep-link) under Partners. Mirrors Support.

import { useEffect, useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { DataTable, type DataTableColumn } from '@drivecars/admin-ui';
import { PageHeader, ToolChip, ToolSearch, Toolbar } from '@drivecars/admin-ui';
import { StatusPill } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  fetchAdminPartnerApplications,
  setPartnerApplicationStatus,
  createPartnerFromApplication,
  type AdminPartnerApplicationListParams,
  type AdminPartnerApplicationRow,
  type PartnerApplicationStatus,
} from '@/lib/admin-api';
import { usePager } from '@/lib/use-pager';

type FilterKey = NonNullable<AdminPartnerApplicationListParams['status']>;

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'in_review', label: 'In review' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

const fmtTime = (iso: string) => {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

interface ActionCtx {
  busyId: number | null;
  setStatus: (id: number, status: PartnerApplicationStatus) => void;
  onConvert: (row: AdminPartnerApplicationRow) => void;
}

const buildColumns = (ctx: ActionCtx): DataTableColumn<AdminPartnerApplicationRow>[] => [
  {
    key: 'company',
    h: 'Agency',
    cell: (r) => (
      <div className="min-w-0">
        <div className="truncate font-medium">{r.companyName}</div>
        <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">
          {r.country}
          {r.website ? ` · ${r.website}` : ''}
        </div>
      </div>
    ),
  },
  {
    key: 'contact',
    h: 'Contact',
    cell: (r) => (
      <div className="min-w-0">
        <div className="truncate font-medium">{r.contactName}</div>
        <div className="truncate text-[11.5px] text-[color:var(--bo-mute)]">
          {r.email} · {r.phone}
        </div>
      </div>
    ),
  },
  { key: 'type', h: 'Type', cell: (r) => r.partnerType ?? '—' },
  { key: 'status', h: 'Status', cell: (r) => <StatusPill status={r.status} /> },
  {
    key: 'time',
    h: 'Received',
    right: true,
    cell: (r) => (
      <span className="tabular-nums text-[11.5px] text-[color:var(--bo-mute)]">
        {fmtTime(r.createdAt)}
      </span>
    ),
  },
  {
    key: 'actions',
    h: 'Actions',
    cell: (r) => {
      const busy = ctx.busyId === r.id;
      return (
        <div className="flex flex-wrap items-center gap-1.5">
          {r.status !== 'in_review' && r.status !== 'approved' ? (
            <RowAction kind="primary" busy={busy} onClick={() => ctx.setStatus(r.id, 'in_review')}>
              Review
            </RowAction>
          ) : null}
          {r.status !== 'rejected' ? (
            <RowAction kind="primary" busy={busy} onClick={() => ctx.onConvert(r)}>
              Create partner
            </RowAction>
          ) : null}
          {r.status !== 'rejected' ? (
            <RowAction kind="danger" busy={busy} onClick={() => ctx.setStatus(r.id, 'rejected')}>
              Reject
            </RowAction>
          ) : null}
        </div>
      );
    },
  },
];

export function PartnerApplications() {
  const [filter, setFilter] = useState<FilterKey>('new');
  const [q, setQ] = useState('');
  const { page, setPage, limit, setLimit } = usePager();
  const queryClient = useQueryClient();

  // Any filter change lands back on page 1.
  useEffect(() => {
    setPage(1);
  }, [filter, q, setPage]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'partner-applications', { filter, q, page }],
    queryFn: ({ signal }) =>
      fetchAdminPartnerApplications(
        { status: filter, q: q.trim() || undefined, page, limit },
        signal,
      ),
    placeholderData: keepPreviousData,
  });

  const setStatusMut = useMutation({
    mutationFn: ({ id, status }: { id: number; status: PartnerApplicationStatus }) =>
      setPartnerApplicationStatus(id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'partner-applications'] });
    },
  });

  const [convert, setConvert] = useState<AdminPartnerApplicationRow | null>(null);

  const cols = buildColumns({
    busyId: setStatusMut.isPending && setStatusMut.variables ? setStatusMut.variables.id : null,
    setStatus: (id, status) => setStatusMut.mutate({ id, status }),
    onConvert: setConvert,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const counts = data?.statusCounts ?? {};
  const mutationError = setStatusMut.error;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Growth"
        title="Partner applications"
        sub={`${total.toLocaleString()} total · ${counts.new ?? 0} new · ${counts.in_review ?? 0} in review. "Create partner" onboards an applicant + reveals their secret.`}
      />
      {error ? (
        <div role="alert" className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
          Couldn&apos;t load applications: {(error as Error).message}
        </div>
      ) : null}
      {mutationError ? (
        <div role="alert" className="rounded-lg border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
          Status update failed: {(mutationError as Error).message}
        </div>
      ) : null}
      <Toolbar>
        <ToolSearch value={q} onChange={setQ} placeholder="Search agency, contact, email, country…" />
        {FILTERS.map((f) => (
          <ToolChip key={f.key} active={filter === f.key} onClick={() => setFilter(f.key)}>
            {f.label}
            {counts[f.key] != null ? (
              <span className="ms-1 tabular-nums text-[10.5px] text-[color:var(--bo-mute)]">
                {counts[f.key]}
              </span>
            ) : null}
          </ToolChip>
        ))}
      </Toolbar>
      <DataTable
        columns={cols}
        rows={rows}
        emptyLabel={isLoading ? 'Loading…' : 'No matching applications.'}
        pagination={{ page, limit, total, onPageChange: setPage, onLimitChange: setLimit }}
      />
      {convert ? (
        <ConvertModal
          application={convert}
          onClose={() => setConvert(null)}
          onCreated={() => {
            void queryClient.invalidateQueries({ queryKey: ['admin', 'partner-applications'] });
          }}
        />
      ) : null}
    </div>
  );
}

const ALL_COMPONENTS = ['search', 'cart', 'checkout', 'payment'] as const;

function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 48) || 'partner'
  );
}

function ConvertModal({
  application,
  onClose,
  onCreated,
}: {
  application: AdminPartnerApplicationRow;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [slug, setSlug] = useState(slugify(application.companyName));
  const [commissionPct, setCommissionPct] = useState('');
  const [components, setComponents] = useState<Set<string>>(new Set(ALL_COMPONENTS));
  const [secret, setSecret] = useState<string | null>(null);
  const [createdSlug, setCreatedSlug] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      createPartnerFromApplication(application.id, {
        slug: slug.trim() || undefined,
        commissionPct: commissionPct.trim() ? Number(commissionPct) / 100 : undefined,
        enabledComponents: ALL_COMPONENTS.filter((c) => components.has(c)),
      }),
    onSuccess: (res) => {
      setSecret(res.oneTimeSecret);
      setCreatedSlug(res.partner.slug);
      onCreated();
    },
  });

  const toggle = (c: string) =>
    setComponents((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {secret ? (
          <div className="space-y-3">
            <h2 className="text-[15px] font-semibold">Partner created: {createdSlug}</h2>
            <p className="text-[12.5px] text-[color:var(--bo-mute)]">
              Copy the secret key now — it&apos;s shown once. Share it with the partner so they can sign
              into the portal. Set commission/origins anytime under Partners.
            </p>
            <code className="block break-all rounded-lg border border-black/10 bg-[#f7f8fa] px-3 py-2 text-[12px]">
              {secret}
            </code>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => void navigator.clipboard.writeText(secret)}
                className="rounded-full border border-black/15 px-3 py-1.5 text-[12.5px] font-medium hover:border-black/40"
              >
                Copy secret
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full bg-[color:var(--bo-ink)] px-3 py-1.5 text-[12.5px] font-medium text-white"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h2 className="text-[15px] font-semibold">Create partner</h2>
              <p className="text-[12.5px] text-[color:var(--bo-mute)]">
                From application: {application.companyName} · {application.email}
              </p>
            </div>
            <label className="block">
              <span className="mb-1 block text-[12px] font-medium">Slug</span>
              <input
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="block w-full rounded-lg border border-black/15 px-3 py-2 text-[13px]"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[12px] font-medium">Commission % (optional)</span>
              <input
                value={commissionPct}
                onChange={(e) => setCommissionPct(e.target.value)}
                type="number"
                min={0}
                max={100}
                step="0.1"
                placeholder="e.g. 8"
                className="block w-full rounded-lg border border-black/15 px-3 py-2 text-[13px]"
              />
            </label>
            <div>
              <span className="mb-1.5 block text-[12px] font-medium">Enabled widgets</span>
              <div className="flex flex-wrap gap-2">
                {ALL_COMPONENTS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggle(c)}
                    className={
                      components.has(c)
                        ? 'rounded-full border border-[color:var(--bo-ink)] bg-[color:var(--bo-ink)] px-3 py-1 text-[12px] capitalize text-white'
                        : 'rounded-full border border-black/15 px-3 py-1 text-[12px] capitalize hover:border-black/40'
                    }
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            {mut.error ? (
              <p className="text-[12.5px] text-[#9f1239]">Create failed: {(mut.error as Error).message}</p>
            ) : null}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-black/15 px-3 py-1.5 text-[12.5px] font-medium hover:border-black/40"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={mut.isPending || !slug.trim()}
                onClick={() => mut.mutate()}
                className="rounded-full bg-[color:var(--bo-ink)] px-4 py-1.5 text-[12.5px] font-medium text-white disabled:opacity-50"
              >
                {mut.isPending ? 'Creating…' : 'Create partner'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
